import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PageLayout from "../../components/PageLayout";
import Button from "../../components/Button";
import { getLoginDetails } from "../../utils/storage";
import UploadIcon from "../../icons/upload.svg";
import "./VirtualGrading.css";

// Mock data for admin items list
const mockItems = [
  {
    id: "item_1",
    title: "Toyota Catalytic Converter",
    user: "John Doe",
    submittedDate: "2024-10-28T19:16:00Z",
    status: "pending",
    priority: "high",
    grade: null,
    price: null,
    thumbnail: "https://via.placeholder.com/80x60/cccccc/666666?text=IMG",
  },
  {
    id: "item_2",
    title: "Ford F-150 Converter",
    user: "Jane Smith",
    submittedDate: "2024-10-27T14:30:00Z",
    status: "graded",
    priority: "medium",
    grade: "A",
    price: 150,
    thumbnail: "https://via.placeholder.com/80x60/cccccc/666666?text=IMG",
  },
  {
    id: "item_3",
    title: "Honda Civic Exhaust",
    user: "Mike Johnson",
    submittedDate: "2024-10-26T09:45:00Z",
    status: "rejected",
    priority: "low",
    grade: null,
    price: null,
    thumbnail: "https://via.placeholder.com/80x60/cccccc/666666?text=IMG",
  },
];

const AdminItemsList = () => {
  const { hierarchy } = getLoginDetails();
  const [items, setItems] = useState(mockItems);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterUser, setFilterUser] = useState("");
  const [sortBy, setSortBy] = useState("submittedDate");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Redirect if not admin
  useEffect(() => {
    if (hierarchy !== 1) {
      window.location.href = "/virtual-grading";
    }
  }, [hierarchy]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "#dc3545";
      case "medium":
        return "#ffc107";
      case "low":
        return "#28a745";
      default:
        return "#6c757d";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "#ffc107";
      case "graded":
        return "#28a745";
      case "rejected":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  const filteredAndSortedItems = items
    .filter((item) => {
      if (filterStatus !== "all" && item.status !== filterStatus) {
        return false;
      }
      if (filterUser && !item.user.toLowerCase().includes(filterUser.toLowerCase())) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (sortBy === "submittedDate") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  if (hierarchy !== 1) {
    return null; // Will redirect
  }

  return (
    <PageLayout 
      heading="Virtual Grading Admin"
      icon={UploadIcon}
    >
      <div className="virtual-grading-container">
        {/* Filters */}
        <div className="vg-filter-bar">
          <div className="vg-filter-content">
            <div className="vg-filter-group">
              <label className="vg-filter-label">Status:</label>
              <select
                className="vg-filter-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="graded">Graded</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="vg-filter-group">
              <label className="vg-filter-label">User:</label>
              <input
                type="text"
                className="vg-filter-input"
                placeholder="Search by user..."
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
              />
            </div>
            <div className="vg-filter-group">
              <label className="vg-filter-label">Sort by:</label>
              <select
                className="vg-filter-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="submittedDate">Submitted Date</option>
                <option value="user">User</option>
                <option value="status">Status</option>
                <option value="priority">Priority</option>
              </select>
            </div>
            <Button className="vg-filter-button">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 16 16"
                className="mr-8"
              >
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
              </svg>
              Apply Filters
            </Button>
          </div>
        </div>

        {/* Items Table */}
        <div className="vg-content">
          <div className="vg-items-table">
            <div className="vg-table-header">
              <div className="vg-table-cell vg-sortable" onClick={() => handleSort("title")}>
                Item
                {sortBy === "title" && (
                  <span className="vg-sort-icon">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </div>
              <div className="vg-table-cell vg-sortable" onClick={() => handleSort("user")}>
                User
                {sortBy === "user" && (
                  <span className="vg-sort-icon">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </div>
              <div className="vg-table-cell vg-sortable" onClick={() => handleSort("submittedDate")}>
                Submitted
                {sortBy === "submittedDate" && (
                  <span className="vg-sort-icon">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </div>
              <div className="vg-table-cell vg-sortable" onClick={() => handleSort("status")}>
                Status
                {sortBy === "status" && (
                  <span className="vg-sort-icon">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </div>
              <div className="vg-table-cell vg-sortable" onClick={() => handleSort("priority")}>
                Priority
                {sortBy === "priority" && (
                  <span className="vg-sort-icon">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </div>
              <div className="vg-table-cell">Grade/Price</div>
              <div className="vg-table-cell">Actions</div>
            </div>
            
            {paginatedItems.length === 0 ? (
              <div className="vg-empty-state">
                <p>No items found matching your criteria</p>
              </div>
            ) : (
              paginatedItems.map((item) => (
                <div key={item.id} className="vg-table-row">
                  <div className="vg-table-cell">
                    <div className="vg-item-cell">
                      <div className="vg-item-thumbnail">
                        <img src={item.thumbnail} alt={item.title} />
                      </div>
                      <div className="vg-item-details">
                        <div className="vg-item-title">{item.title}</div>
                        <div className="vg-item-id">ID: {item.id.toUpperCase()}</div>
                      </div>
                    </div>
                  </div>
                  <div className="vg-table-cell">{item.user}</div>
                  <div className="vg-table-cell">{formatDate(item.submittedDate)}</div>
                  <div className="vg-table-cell">
                    <span
                      className="vg-status-badge"
                      style={{ backgroundColor: getStatusColor(item.status) }}
                    >
                      {item.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="vg-table-cell">
                    <span
                      className="vg-priority-badge"
                      style={{ backgroundColor: getPriorityColor(item.priority) }}
                    >
                      {item.priority.toUpperCase()}
                    </span>
                  </div>
                  <div className="vg-table-cell">
                    {item.status === "graded" ? (
                      <div className="vg-grade-price">
                        <span className="vg-grade">Grade: {item.grade}</span>
                        <span className="vg-price">${item.price}</span>
                      </div>
                    ) : (
                      <span className="vg-no-grade">-</span>
                    )}
                  </div>
                  <div className="vg-table-cell">
                    <div className="vg-action-buttons">
                      <Link
                        to={`/virtual-grading/item/${item.id}`}
                        className="vg-action-btn"
                      >
                        View
                      </Link>
                      {item.status === "pending" && (
                        <Link
                          to={`/virtual-grading/admin/grade/${item.id}`}
                          className="vg-action-btn primary"
                        >
                          Grade
                        </Link>
                      )}
                      <button className="vg-action-btn chat">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8 15c4.418 0 8-3.134 8-7s-3.582-7-8-7-8 3.134-8 7c0 1.76.743 3.37 1.97 4.6-.097 1.016-.417 2.13-.771 2.966-.079.186-.074.402.027.533.098.127.24.2.403.2.401 0 .744-.115 1.103-.33 1.927-.88 3.26-2.41 3.843-3.404C7.884 13.43 7.961 13.5 8 13.5c.039 0 .116-.07.189-.167.583-.994 1.916-2.524 3.843-3.404.359.215.702.33 1.103.33.163 0 .305-.073.403-.2.101-.131.106-.347.027-.533-.354-.836-.674-1.95-.771-2.966C15.257 11.37 16 9.76 16 8c0-3.866-3.582-7-8-7z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="vg-pagination">
              <button
                className="vg-page-btn"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`vg-page-btn ${currentPage === page ? "active" : ""}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              
              <button
                className="vg-page-btn"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default AdminItemsList;
